import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Stack,
  LinearProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import PageTitle from '../../components/PageTitle';
import salesmanService from '../../services/salesmanService';
import locationService from '../../services/locationService';
import { getCurrentUser } from '../../services/authService';
import { downloadXlsxFromRows } from '../../utils/inventoryListExport';

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    py: 0.65,
    px: 1.5,
    fontSize: '0.8125rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiTableCell-head': {
    py: 0.85,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    bgcolor: 'action.hover',
  },
};

const SalesmenPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const nameInputRef = useRef(null);
  const [salesmenRaw, setSalesmenRaw] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [locationFilter, setLocationFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    locationId: '',
    companyId: '',
  });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const fetchSalesmen = useCallback(async () => {
    try {
      setLoading(true);
      const data = await salesmanService.getAll({
        page: 0,
        size: 2000,
        locationId: locationFilter || undefined,
      });
      setSalesmenRaw(data.content || []);
    } catch (error) {
      console.error('Error fetching salesmen:', error);
      enqueueSnackbar('Failed to load salesmen', { variant: 'error' });
      setSalesmenRaw([]);
    } finally {
      setLoading(false);
    }
  }, [locationFilter, enqueueSnackbar]);

  const fetchLocations = async () => {
    try {
      const res = await locationService.list({ page: 0, size: 200, sort: 'name', direction: 'ASC' });
      setLocations(res.content || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocations([]);
    }
  };

  useEffect(() => {
    fetchSalesmen();
  }, [fetchSalesmen]);

  useEffect(() => {
    fetchLocations();
  }, []);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredSalesmen = useMemo(() => {
    if (!normalizedSearch) return salesmenRaw;
    return salesmenRaw.filter((row) => {
      const hay = [row.name, row.phone, row.email, row.address, row.locationName, String(row.id ?? '')]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(normalizedSearch);
    });
  }, [salesmenRaw, normalizedSearch]);

  const displayedSalesmen = useMemo(
    () => filteredSalesmen.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredSalesmen, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [searchQuery, locationFilter]);

  const handleExportSalesmenExcel = useCallback(() => {
    const rows = filteredSalesmen.map((row, idx) => ({
      'S.No': idx + 1,
      Name: row.name || '',
      Phone: row.phone || '',
      Email: row.email || '',
      Location: row.locationName || '',
      Address: row.address || '',
    }));
    const ok = downloadXlsxFromRows(
      `salesmen_${new Date().toISOString().split('T')[0]}.xlsx`,
      'Salesmen',
      rows
    );
    if (!ok) enqueueSnackbar('Nothing to export', { variant: 'info' });
  }, [filteredSalesmen, enqueueSnackbar]);

  const handleChangePage = (e, newPage) => {
    const maxPage = Math.max(0, Math.ceil(filteredSalesmen.length / rowsPerPage) - 1);
    setPage(Math.min(newPage, maxPage));
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (salesman = null) => {
    fetchLocations();
    const companyId = getCurrentUser()?.companyId ? parseInt(getCurrentUser().companyId, 10) : null;
    if (salesman) {
      setFormData({
        name: salesman.name || '',
        phone: salesman.phone || '',
        email: salesman.email || '',
        address: salesman.address || '',
        locationId: salesman.locationId ?? '',
        companyId: salesman.companyId || companyId,
      });
      setEditMode(true);
      setEditId(salesman.id);
    } else {
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        locationId: locationFilter || '',
        companyId: companyId || '',
      });
      setEditMode(false);
      setEditId(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!formData.locationId) errors.locationId = 'Location is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      ...formData,
      companyId: formData.companyId || getCurrentUser()?.companyId,
      locationId: parseInt(formData.locationId, 10),
    };
    try {
      setLoading(true);
      if (editMode) {
        await salesmanService.update(editId, payload);
        enqueueSnackbar('Salesman updated successfully', { variant: 'success' });
      } else {
        await salesmanService.create(payload);
        enqueueSnackbar('Salesman created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchSalesmen();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to save salesman';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this salesman?')) return;
    try {
      setLoading(true);
      await salesmanService.delete(id);
      enqueueSnackbar('Salesman deleted successfully', { variant: 'success' });
      fetchSalesmen();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to delete salesman', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <PageTitle title="Salesmen" />
      <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2, md: 3 }, py: 1.5, bgcolor: '#fafbfc', minHeight: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Salesmen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
              Create salesmen for each location. Select a salesman at POS when placing a bill to attribute sales for reports.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Location</InputLabel>
              <Select
                value={locationFilter}
                label="Location"
                onChange={(e) => {
                  setLocationFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All locations</MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search name, phone, email, location…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { sm: 1 } }}
            />
            <Button variant="outlined" color="primary" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportSalesmenExcel}>
              Export Excel
            </Button>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={fetchSalesmen} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog(null)}>
              Add Salesman
            </Button>
          </Stack>
        </Box>

        <Box sx={{ width: '100%', maxWidth: '95%', mx: 'auto', mt: 1 }}>
          <Paper variant="outlined" elevation={0} sx={{ borderRadius: 1, overflow: 'hidden' }}>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell width="22%">Name</TableCell>
                    <TableCell width="14%">Phone</TableCell>
                    <TableCell width="20%">Email</TableCell>
                    <TableCell width="22%">Location</TableCell>
                    <TableCell width="14%">Address</TableCell>
                    <TableCell width="8%" align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && salesmenRaw.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : displayedSalesmen.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        No salesmen found. Add one for your location.
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedSalesmen.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap title={row.name}>
                            {row.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {row.phone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={row.email || ''}>
                            {row.email || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={row.locationName || ''}>
                            {row.locationName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={row.address || ''}>
                            {row.address?.trim() ? row.address : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredSalesmen.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              size="small"
            />
          </Paper>
        </Box>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          TransitionProps={{
            onEntered: () => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  nameInputRef.current?.focus({ preventScroll: true });
                });
              });
            },
          }}
        >
          <DialogTitle>{editMode ? 'Edit Salesman' : 'Add Salesman'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  inputRef={nameInputRef}
                  name="name"
                  label="Name *"
                  value={formData.name}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  error={Boolean(formErrors.name)}
                  helperText={formErrors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="phone"
                  label="Phone *"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  error={Boolean(formErrors.phone)}
                  helperText={formErrors.phone}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label="Email (optional)"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="address"
                  label="Address (optional)"
                  value={formData.address}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={Boolean(formErrors.locationId)} size="small">
                  <InputLabel>Location *</InputLabel>
                  <Select name="locationId" value={formData.locationId ?? ''} onChange={handleInputChange} label="Location *">
                    <MenuItem value="">Select location</MenuItem>
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.locationId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {formErrors.locationId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={loading}>
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default SalesmenPage;
